import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useBeforeUnload, useBlocker } from "react-router-dom";
import { hasSettingsDraft, type SettingsDraftState } from "../hooks/settings-draft-state";
import { SettingsDiscardDialog } from "./settings-discard-dialog";

type Registry = (id: string, state: SettingsDraftState | null) => void;
const DraftContext = createContext<Registry>(() => undefined);
export const useSettingsDraftRegistry = () => useContext(DraftContext);

/** One router blocker for the settings surface; individual forms only register their state. */
export function SettingsDraftProvider({ children }: { children: ReactNode }) {
  const [drafts, setDrafts] = useState<Record<string, SettingsDraftState>>({});
  const register = useCallback<Registry>(
    (id, state) =>
      setDrafts((previous) => {
        if (!state) {
          const next = { ...previous };
          delete next[id];
          return next;
        }
        if (previous[id]?.dirty === state.dirty && previous[id]?.pending === state.pending) return previous;
        return { ...previous, [id]: state };
      }),
    [],
  );
  const states = Object.values(drafts);
  const guarded = hasSettingsDraft(states);
  const pending = states.some((state) => state.pending);
  const blocker = useBlocker(guarded);
  useBeforeUnload(
    useCallback(
      (event) => {
        if (guarded) {
          event.preventDefault();
          event.returnValue = "";
        }
      },
      [guarded],
    ),
  );
  useEffect(() => {
    if (!guarded && blocker.state === "blocked") blocker.reset();
  }, [guarded, blocker]);
  const value = useMemo(() => register, [register]);
  return (
    <DraftContext.Provider value={value}>
      {children}
      <SettingsDiscardDialog open={blocker.state === "blocked"} pending={pending} onCancel={() => blocker.reset?.()} onDiscard={() => blocker.proceed?.()} />
    </DraftContext.Provider>
  );
}
